import {
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import axios from 'axios';
import { environment } from 'src/environments/environment';
import { ClaimSums } from 'src/types/claim-sum';
import { PayeeCasesService } from '../payee-cases.service';
import i18n from './translate';
import { DecimalPipe, DatePipe, KeyValuePipe } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';
import { PayeeIconComponent } from '../../payee-icon/payee-icon.component';

export type HistoricCase = {
  state_label: string;
  stage_label: string;
  payee_case_reference_id: string;
  started_at: string | null;
};

export type ExpandedElementDetails = {
  payee_case: {
    id: string;
    payee_case_reference_id: string;
    description: string;
    legal_relationship_files: {
      file_name: string;
      storage_url: string;
    }[];
    country_iso: 'HU' | 'GB';
    legal_relationship_specification: string;
    bailiff_contact_url?: string | null;
    history: HistoricCase[];
  };
  debtor: {
    name: string;
    address: string | null;
    email: string | null;
    phone: string | null;
    representative: {
      name: string | null;
      type: string | null;
      address: string | null;
    };
  };
  claims: {
    due_date_at: string;
    remaining_amount: number;
    original_amount: number;
    currency: string;
    files: {
      file_name: string;
      storage_url: string;
    }[];
  }[];
  claims_sums: ClaimSums;
  displayed_claims_sums: ClaimSums;
  hidden_claims_sums: ClaimSums;
  hiddenClaims: {
    remaining_amount: number;
    original_amount: number;
    currency: string;
    label: string;
  }[];
  proforma_invoices: {
    file_name: string;
    storage_url: string;
    is_paid: boolean;
  }[];
  invoices: {
    file_name: string;
    storage_url: string;
  }[];
  external_ids: {
    type: string;
    value: string;
  }[];
};

@Component({
  selector: 'app-case-details',
  templateUrl: './case-details.component.html',
  styleUrls: ['./case-details.component.scss'],
  imports: [
    DecimalPipe,
    DatePipe,
    KeyValuePipe,
    TranslateModule,
    PayeeIconComponent,
    MatTooltip,
  ],
})
export class CaseDetailsComponent {
  private snackbar = inject(MatSnackBar);
  private translate = inject(TranslateService);
  private payeeCasesService = inject(PayeeCasesService);

  private static translationsLoaded = false;

  readonly caseId = input.required<string>();

  copyTooltipDisabled = true;
  readonly expandedElementDetails = signal<ExpandedElementDetails | undefined>(
    undefined,
  );
  readonly isLoading = signal(true);
  readonly showParent = computed(
    () => (this.expandedElementDetails()?.payee_case.history.length ?? 0) > 1,
  );

  constructor() {
    if (!CaseDetailsComponent.translationsLoaded) {
      for (const [lang, translation] of Object.entries(i18n)) {
        this.translate.setTranslation(lang, translation, true);
      }
      CaseDetailsComponent.translationsLoaded = true;
    }

    effect(() => {
      const caseId = this.caseId();
      void this.loadDetails(caseId);
    });
  }

  private async loadDetails(caseId: string): Promise<void> {
    this.isLoading.set(true);
    this.expandedElementDetails.set(undefined);

    try {
      const url = environment.baseUrl + `/api/case-details/${caseId}`;
      const result = await axios.get<ExpandedElementDetails>(url);

      if (caseId !== this.caseId()) {
        return;
      }

      this.expandedElementDetails.set(result.data);
    } catch (error) {
      if (caseId !== this.caseId()) {
        return;
      }

      this.snackbar.open(
        this.translate.instant(
          'CASE_DETAILS.ERROR_MESSAGES.UNKNOWN_ERROR_WHILE_LOADING_DETAILS',
        ),
        this.translate.instant('CASE_DETAILS.OK'),
        {
          duration: 5000,
        },
      );
      console.error('Error while loading case details', error);
    } finally {
      if (caseId === this.caseId()) {
        this.isLoading.set(false);
      }
    }
  }

  async copyText(text: string, labelKey?: string) {
    await navigator.clipboard.writeText(text);
    const label = labelKey ? this.translate.instant(labelKey) : undefined;
    const message = label
      ? this.translate.instant('CASE_DETAILS.COPIED_LABELED', {
          label,
          value: text,
        })
      : this.translate.instant('CASE_DETAILS.COPIED_VALUE', { value: text });
    this.snackbar.open(
      message,
      this.translate.instant('CASE_DETAILS.OK'),
      { duration: 3000 },
    );
  }

  filterToHistoricCase(element: HistoricCase): void {
    this.payeeCasesService.payee_case_reference_id.next(
      element.payee_case_reference_id,
    );
  }
}
